import { ErrorHandler, Injectable, inject } from '@angular/core';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { environment } from 'environments/environment';
import { isLoginRequiredError, normalizeTelemetryPath } from './posthog-diagnostics';
import { PosthogService } from './posthog.service';

const CHUNK_RELOAD_KEY = 'stm_store_chunk_reload_attempted';

/**
 * Sends errors to Application Insights and PostHog, then mirrors Angular's default
 * ErrorHandler console output in non-production for local debugging.
 */
@Injectable()
export class LoggingErrorHandler implements ErrorHandler {
    private readonly appInsightsErrorHandler = inject(ApplicationinsightsAngularpluginErrorService);
    private readonly posthogService = inject(PosthogService);

    constructor() {
        this.clearChunkReloadMarkers();
    }

    handleError(error: unknown): void {
        if (isLoginRequiredError(error)) {
            this.posthogService.capture('authInteractiveLoginRequired', {
                route: normalizeTelemetryPath(window.location.pathname),
                source: 'angular.errorHandler'
            });
            return;
        }

        if (this.isChunkLoadError(error)) {
            this.handleChunkLoadError(error);
            return;
        }

        this.appInsightsErrorHandler.handleError(error);
        this.posthogService.captureException(error, { source: 'angular.errorHandler' });

        if (!environment.production) {
            console.error('ERROR', error);
        }
    }

    private handleChunkLoadError(error: unknown): void {
        const message = this.getErrorMessage(error);
        this.posthogService.capture('chunkLoadFailed', {
            source: 'angular.errorHandler',
            route: window.location.pathname,
            message
        });

        const reloadKey = `${CHUNK_RELOAD_KEY}:${window.location.pathname}`;
        if (sessionStorage.getItem(reloadKey)) {
            this.appInsightsErrorHandler.handleError(error);
            this.posthogService.captureException(error, {
                source: 'angular.errorHandler',
                reloadAttempted: true
            });
            return;
        }

        sessionStorage.setItem(reloadKey, 'true');
        window.location.reload();
    }

    private isChunkLoadError(error: unknown): boolean {
        const message = this.getErrorMessage(error).toLowerCase();
        return message.includes('failed to fetch dynamically imported module') || message.includes('importing a module script failed') || message.includes('not a valid javascript mime type') || message.includes('chunkloaderror');
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }

        if (typeof error === 'string') {
            return error;
        }

        if (!this.isObjectRecord(error)) {
            return String(error);
        }

        if ('message' in error) {
            return String(error.message);
        }

        if ('error' in error) {
            return this.getErrorMessage(error.error);
        }

        if ('rejection' in error) {
            return this.getErrorMessage(error.rejection);
        }

        return String(error);
    }

    private isObjectRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private clearChunkReloadMarkers(): void {
        Object.keys(sessionStorage)
            .filter((key) => key.startsWith(`${CHUNK_RELOAD_KEY}:`))
            .forEach((key) => sessionStorage.removeItem(key));
    }
}
